/**
 * AI Playground — enter a prompt, see what the tenant's configured routing
 * actually returns.
 *
 * This is the AI Center's proving ground: it exercises the same ModelRouter
 * the runtime uses, so it answers "is our AI set up correctly?" before any
 * workload depends on it. The routing summary strip at the top shows which
 * provider and model a run will hit, so a surprising answer can be traced to
 * the configuration rather than guessed at.
 *
 * Gated on `admin:extensions:manage` rather than `:view` — a run is a real,
 * billable model call, so read-only admins get the routing summary and a
 * pointer to the config page instead of a Run button.
 */
import { useEffect, useState, type JSX } from 'react'
import { Loader2, Server, Sparkles } from 'lucide-react'

import RequirePermission from '../../components/auth/RequirePermission.js'
import AiTestConsole from '../../components/admin/ai/AiTestConsole.js'
import { Section } from '../../components/admin/ai/ui.js'
import { api } from '../../api/client.js'
import { useToast } from '../../components/system/ToastProvider.js'

interface AiProvidersResponse {
  configured: string[]
  aliases: Record<string, string>
  defaultProvider: string
  fallbackChain: string[]
}

export default function AiPlaygroundPage(): JSX.Element {
  const toast = useToast()
  const [routing, setRouting] = useState<AiProvidersResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    void (async () => {
      try {
        setRouting(await api.get<AiProvidersResponse>('/admin/ai/providers'))
      } catch (err) {
        toast.error('Failed to load routing summary', String(err))
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  return (
    <RequirePermission permission="admin:extensions:view">
      {/* Routing summary — what a run will actually hit. */}
      <Section
        icon={<Server className="h-4 w-4" />}
        title="Active routing"
        description="Resolved from the platform defaults plus this tenant's overrides."
      >
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading routing…
          </div>
        ) : routing ? (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                Default provider
              </p>
              <code className="rounded-sm border border-gray-200 bg-gray-50 px-2 py-1 font-mono text-xs text-gray-700">
                {routing.defaultProvider}
              </code>
            </div>
            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                Fallback chain
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                {routing.fallbackChain.map((p, i) => (
                  <span key={p} className="inline-flex items-center gap-1.5">
                    {i > 0 && <span className="text-gray-300">→</span>}
                    <code className="rounded-sm border border-gray-200 bg-gray-50 px-2 py-1 font-mono text-xs text-gray-700">
                      {p}
                    </code>
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                Reachable now
              </p>
              <div className="flex flex-wrap gap-1.5">
                {routing.configured.length > 0 ? (
                  routing.configured.map((p) => (
                    <span
                      key={p}
                      className="inline-flex items-center gap-1.5 rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-1 font-mono text-xs text-emerald-700"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
                      {p}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-gray-400">none configured</span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Routing summary unavailable.</p>
        )}
      </Section>

      {/* Console */}
      <RequirePermission
        permission="admin:extensions:manage"
        fallback={
          <Section
            icon={<Sparkles className="h-4 w-4" />}
            title="Prompt"
            description="Running a prompt requires the AI manage permission."
          >
            <p className="text-sm text-gray-500">
              You can view the routing above, but running a prompt issues a billable model call and
              needs <code className="rounded-sm bg-gray-100 px-1 py-0.5 text-xs">admin:extensions:manage</code>.
            </p>
          </Section>
        }
      >
        <Section
          icon={<Sparkles className="h-4 w-4" />}
          title="Prompt"
          description="Runs through the routing above — same resolution and fallback the runtime uses. This is a real, billable model call."
        >
          <AiTestConsole showHistory showSystemPrompt promptRows={6} />
        </Section>
      </RequirePermission>
    </RequirePermission>
  )
}

